import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut, RefreshCcw, Filter, ArrowUpRight, DollarSign, CalendarDays, CheckCircle2,
  ClipboardList, Trash2, Eye, X, Home, Mail, Phone as PhoneIcon, MapPin, Sparkles, AlertTriangle,
  Flame, Save, Settings, Link2, Copy, CreditCard, BadgeCheck, MessageSquareQuote, Plus, Star, Edit3,
  Eye as EyeIcon, EyeOff, Image as ImageIcon,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";
import { IS_QUICKBOOKS, PROVIDER_LABEL } from "@/lib/payments";

const STATUS_LABELS = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-800 border-amber-300" },
  confirmed: { label: "Confirmed", color: "bg-blue-100 text-blue-800 border-blue-300" },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800 border-red-300" },
};

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [promoSettings, setPromoSettings] = useState(null);
  const navigate = useNavigate();

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, b, p] = await Promise.all([
        api.get("/admin/bookings/stats"),
        api.get("/admin/bookings"),
        api.get("/settings/promo"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setPromoSettings(p.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login");
      } else {
        toast.error("Failed to load bookings");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    navigate("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await api.patch(`/admin/bookings/${id}`, { status });
      const updated = res.data;
      if (status === "completed" && updated.remainder_payment_url) {
        toast.success("Booking completed — payment link is ready to send", { duration: 5000 });
      } else {
        toast.success(`Status updated to ${status}`);
      }
      loadAll();
      if (selected?.id === id) setSelected(updated);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const updatePaymentStatus = async (id, payment_status) => {
    try {
      const res = await api.patch(`/admin/bookings/${id}`, { payment_status });
      toast.success(`Payment marked as ${payment_status.replace(/_/g, " ")}`);
      loadAll();
      if (selected?.id === id) setSelected(res.data);
    } catch {
      toast.error("Failed to update payment status");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Booking deleted");
      setSelected(null);
      loadAll();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const filtered = filter === "all" ? bookings : bookings.filter(b => b.status === filter);

  return (
    <div className="min-h-screen bg-[var(--brand-cream)]" data-testid="admin-dashboard-page">
      {/* Top bar */}
      <header className="bg-white border-b border-[#e6e0d4] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <img src={BRAND.logoUrl} alt="Logo" className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <div className="font-bold text-sm text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>Top-Notch Pro Admin</div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--brand-gold)] font-bold">Bookings Console</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={loadAll} className="btn-outline" style={{ padding: "8px 16px", fontSize: "13px" }} data-testid="admin-refresh-btn">
              <RefreshCcw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button onClick={logout} className="btn-primary" style={{ padding: "8px 16px", fontSize: "13px" }} data-testid="admin-logout-btn">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        {/* PROMO SETTINGS */}
        {promoSettings && (
          <PromoSettingsPanel
            settings={promoSettings}
            onSaved={(s) => setPromoSettings(s)}
          />
        )}

        {/* TESTIMONIALS MANAGER */}
        <TestimonialsManager />

        {/* TODAY & TOMORROW REMINDER PANEL */}
        <UpcomingJobsPanel bookings={bookings} />

        {/* STATS */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {stats && [
            { label: "Total Bookings", value: stats.total, icon: ClipboardList, color: "var(--brand-teal)" },
            { label: "Pending", value: stats.pending, icon: AlertTriangle, color: "#d97706" },
            { label: "Confirmed", value: stats.confirmed, icon: CalendarDays, color: "#2563eb" },
            { label: "Completed", value: stats.completed, icon: CheckCircle2, color: "#059669" },
            { label: "Est. Revenue", value: `$${stats.estimated_revenue.toLocaleString()}`, icon: DollarSign, color: "var(--brand-gold-deep)" },
          ].map((s) => (
            <div key={s.label} className="card-elevated p-5" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider font-bold text-slate-500">{s.label}</span>
                <s.icon className="w-4 h-4" style={{ color: s.color }} />
              </div>
              <div className="text-2xl font-extrabold" style={{ fontFamily: "Outfit", color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* TABLE */}
        <div className="card-elevated overflow-hidden">
          <div className="p-5 border-b border-[#e6e0d4] flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
            <div>
              <h2 className="font-bold text-lg text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>All Bookings</h2>
              <p className="text-xs text-slate-500">{filtered.length} of {bookings.length} shown</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-4 h-4 text-slate-400" />
              {["all", "pending", "confirmed", "completed", "cancelled"].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  data-testid={`filter-${s}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition ${
                    filter === s ? "bg-[var(--brand-teal)] text-white" : "bg-white border border-[#dcd5c6] text-slate-600 hover:border-[var(--brand-teal)]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {(() => {
            if (loading) {
              return <div className="p-12 text-center text-slate-500">Loading bookings...</div>;
            }
            if (filtered.length === 0) {
              return (
                <div className="p-12 text-center text-slate-500" data-testid="empty-state">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  No bookings {filter !== "all" ? `in ${filter}` : "yet"}.
                </div>
              );
            }
            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bookings-table">
                  <thead className="bg-[var(--brand-cream-soft)] text-slate-600 uppercase text-xs">
                    <tr>
                      <th className="px-5 py-3 text-left font-semibold">Customer</th>
                      <th className="px-5 py-3 text-left font-semibold">Service</th>
                      <th className="px-5 py-3 text-left font-semibold">Date</th>
                      <th className="px-5 py-3 text-right font-semibold">Estimate</th>
                      <th className="px-5 py-3 text-left font-semibold">Status</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((b) => {
                      const status = STATUS_LABELS[b.status] || STATUS_LABELS.pending;
                      return (
                        <tr key={b.id} className="border-t border-[#e6e0d4] hover:bg-[var(--brand-cream-soft)]/40" data-testid={`booking-row-${b.id}`}>
                          <td className="px-5 py-4">
                            <div className="font-semibold text-[var(--brand-teal)]">{b.full_name}</div>
                            <div className="text-xs text-slate-500">{b.email}</div>
                          </td>
                          <td className="px-5 py-4">
                            <div className="font-semibold capitalize">{b.service_type} Clean</div>
                            <div className="text-xs text-slate-500">{b.bedrooms} bd • {b.bathrooms} ba • {b.frequency.replace("_", " ")}</div>
                          </td>
                          <td className="px-5 py-4 text-slate-700">{b.preferred_date}<div className="text-xs text-slate-500 capitalize">{b.preferred_time}</div></td>
                          <td className="px-5 py-4 text-right font-bold text-[var(--brand-teal)]">${b.service_estimate.toFixed(2)}</td>
                          <td className="px-5 py-4">
                            <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold border ${status.color}`}>{status.label}</span>
                          </td>
                          <td className="px-5 py-4 text-right">
                            <button onClick={() => setSelected(b)} className="btn-outline" style={{ padding: "6px 14px", fontSize: "12px" }} data-testid={`view-booking-${b.id}`}>
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>

        {/* QUOTE REQUESTS PANEL */}
        <QuoteRequestsPanel />
      </div>

      {/* DETAIL DRAWER */}
      {selected && (
        <BookingDetail booking={selected} onClose={() => setSelected(null)} onStatusChange={updateStatus} onPaymentChange={updatePaymentStatus} onDelete={remove} />
      )}
    </div>
  );
}

function BookingDetail({ booking, onClose, onStatusChange, onPaymentChange, onDelete }) {
  const addOnList = Object.entries(booking.add_ons || {}).filter(([_, v]) => v).map(([k]) => k);
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="booking-detail-modal">
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} />
      <div className="relative w-full max-w-lg h-full bg-white shadow-2xl overflow-y-auto animate-[fadeUp_0.25s_ease]">
        <div className="sticky top-0 bg-white border-b border-[#e6e0d4] px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-[var(--brand-teal)] text-lg" style={{ fontFamily: "Outfit" }}>Booking Details</h2>
            <p className="text-xs font-mono text-slate-400">{booking.id}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="close-detail-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status switcher */}
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Update Status</div>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(STATUS_LABELS).map(([s, info]) => (
                <button
                  key={s}
                  onClick={() => onStatusChange(booking.id, s)}
                  className={`px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border-2 transition ${
                    booking.status === s ? "border-[var(--brand-teal)] bg-[var(--brand-teal)] text-white" : "border-[#dcd5c6] bg-white text-slate-600 hover:border-[var(--brand-teal)]"
                  }`}
                  data-testid={`set-status-${s}`}
                >
                  {info.label}
                </button>
              ))}
            </div>
          </div>

          <DetailBlock title="Customer">
            <DetailRow icon={Home} label="Name" value={booking.full_name} />
            <DetailRow icon={Mail} label="Email" value={booking.email} />
            <DetailRow icon={PhoneIcon} label="Phone" value={booking.phone} />
            <DetailRow icon={MapPin} label="Address" value={`${booking.address}, ${booking.city}, ${booking.state} ${booking.zip_code}`} />
            <SendConfirmationButton booking={booking} />
          </DetailBlock>

          <DetailBlock title="Service">
            <DetailRow icon={Sparkles} label="Type" value={`${booking.service_type === "deep" ? "Deep" : "Standard"} Cleaning`} />
            <DetailRow icon={Home} label="Home" value={`${booking.bedrooms} bd • ${booking.bathrooms} ba`} />
            <DetailRow icon={CalendarDays} label="Date / Time" value={`${booking.preferred_date} • ${booking.preferred_time}`} />
            <DetailRow icon={RefreshCcw} label="Frequency" value={booking.frequency.replace("_", " ")} />
            {addOnList.length > 0 && (
              <DetailRow icon={CheckCircle2} label="Add-ons" value={addOnList.join(", ")} />
            )}
            {booking.notes && (
              <DetailRow icon={ClipboardList} label="Notes" value={booking.notes} />
            )}
            {booking.promo_unlocked === "5th_visit_free" && (
              <DetailRow icon={CheckCircle2} label="Promo" value="🎁 5th weekly visit FREE — auto-apply on 5th clean" />
            )}
            <div className="mt-2 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-[11px] text-slate-600 flex items-center gap-2" data-testid="consent-status-row">
              {booking.consent_communications ? (
                <><BadgeCheck className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" /> <span><strong className="text-emerald-700">Email / text consent:</strong> Customer opted in.</span></>
              ) : (
                <><AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" /> <span><strong className="text-amber-700">No email/text consent</strong> — use phone only.</span></>
              )}
            </div>
            <SendReminderButton booking={booking} />
          </DetailBlock>

          <DetailBlock title="Pricing">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Base price</span><span className="font-semibold">${booking.base_price.toFixed(2)}</span></div>
              {booking.discount_amount > 0 && <div className="flex justify-between text-[var(--brand-gold-deep)]"><span>Discount</span><span className="font-semibold">−${booking.discount_amount.toFixed(2)}</span></div>}
              {booking.addons_total > 0 && <div className="flex justify-between"><span>Add-ons</span><span className="font-semibold">+${booking.addons_total.toFixed(2)}</span></div>}
              <div className="flex justify-between border-t border-dashed pt-2"><span className="font-bold">Service estimate</span><span className="font-extrabold text-[var(--brand-teal)]">${booking.service_estimate.toFixed(2)}</span></div>
              <div className="flex justify-between text-xs"><span className="text-slate-500">Deposit (today)</span><span className="text-[var(--brand-gold-deep)] font-bold">$50.00</span></div>
              <div className="flex justify-between text-xs"><span className="text-slate-500">Remaining</span><span className="font-bold">${booking.remaining_balance.toFixed(2)}</span></div>
              <div className="flex justify-between text-xs border-t border-dashed pt-2 mt-2">
                <span className="text-slate-500">Payment status</span>
                <span className="font-bold uppercase tracking-wider text-[10px]">{(booking.payment_status || "deposit_pending").replace(/_/g, " ")}</span>
              </div>
            </div>
            {(booking.payment_status === "deposit_pending" || !booking.payment_status) && (
              <button
                onClick={() => {
                  if (window.confirm(`Mark this $50 deposit as paid? Confirm only after you've verified payment in your QuickBooks/payments dashboard.`)) {
                    onPaymentChange(booking.id, "deposit_paid");
                  }
                }}
                className="mt-3 w-full px-3 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition"
                data-testid="mark-deposit-paid-btn"
              >
                <BadgeCheck className="w-4 h-4" /> Mark Deposit Paid (Manual)
              </button>
            )}
            {booking.payment_status === "deposit_paid" && booking.remaining_balance > 0 && (
              <button
                onClick={() => {
                  if (window.confirm(`Mark this booking as fully paid? Confirm only after you've received the remaining $${booking.remaining_balance.toFixed(2)}.`)) {
                    onPaymentChange(booking.id, "fully_paid");
                  }
                }}
                className="mt-3 w-full px-3 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition"
                data-testid="mark-fully-paid-btn"
              >
                <BadgeCheck className="w-4 h-4" /> Mark Fully Paid (Manual)
              </button>
            )}
          </DetailBlock>

          <RemainderPaymentLink booking={booking} />

          <button onClick={() => onDelete(booking.id)} className="w-full px-4 py-3 rounded-lg border-2 border-red-300 text-red-600 font-semibold hover:bg-red-50 transition flex items-center justify-center gap-2" data-testid="delete-booking-btn">
            <Trash2 className="w-4 h-4" /> Delete Booking
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailBlock({ title, children }) {
  return (
    <div>
      <h3 className="font-bold text-[var(--brand-teal)] mb-3 text-sm uppercase tracking-wider" style={{ fontFamily: "Outfit" }}>{title}</h3>
      <div className="space-y-2 bg-[var(--brand-cream-soft)] p-4 rounded-lg">{children}</div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon className="w-4 h-4 text-[var(--brand-gold)] mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{label}</div>
        <div className="text-slate-800 font-medium break-words">{value}</div>
      </div>
    </div>
  );
}

function UpcomingJobsPanel({ bookings }) {
  // Compute today and tomorrow date strings in local time (YYYY-MM-DD)
  const toIso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const todayStr = toIso(today);
  const tomorrowStr = toIso(tomorrow);

  const eligible = (bookings || []).filter((b) =>
    [b.preferred_date].includes(todayStr) || b.preferred_date === tomorrowStr
  ).filter((b) => b.status !== "cancelled" && b.status !== "completed");

  const tomorrowJobs = eligible.filter((b) => b.preferred_date === tomorrowStr);
  const todayJobs = eligible.filter((b) => b.preferred_date === todayStr);

  if (eligible.length === 0) return null;

  const Row = ({ b }) => (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-white border border-[#e6e0d4]" data-testid={`upcoming-job-${b.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-[var(--brand-teal)] truncate" style={{ fontFamily: "Outfit" }}>{b.full_name}</div>
        <div className="text-[11px] text-slate-500 truncate">
          {(b.service_type === "deep" ? "Deep" : "Standard")} • {(b.preferred_time || "").replace(/_/g, " ")} • {b.city}
        </div>
        {!b.consent_communications && (
          <div className="text-[10px] text-amber-700 mt-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> No consent — call only
          </div>
        )}
      </div>
      <div className="w-28 flex-shrink-0">
        <SendReminderButton booking={b} compact />
        {!b.consent_communications && (
          <a href={`tel:${b.phone}`} className="mt-1.5 inline-flex items-center justify-center gap-1 w-full px-2 py-1.5 rounded-md bg-slate-700 hover:bg-slate-900 text-white text-[10px] font-bold uppercase tracking-wider transition" data-testid={`call-customer-${b.id}`}>
            <PhoneIcon className="w-3 h-3" /> Call
          </a>
        )}
      </div>
    </div>
  );

  return (
    <div className="card-elevated p-5 md:p-6 mb-8 border-2 border-[var(--brand-gold)]" data-testid="upcoming-jobs-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[var(--brand-gold)] text-white flex items-center justify-center flex-shrink-0">
          <CalendarDays className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-lg text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>Upcoming Jobs — Send Reminders</h3>
          <p className="text-xs text-slate-500">One-click email or text reminder for today's & tomorrow's bookings. Bookings without communications consent show a Call button instead.</p>
        </div>
      </div>

      {tomorrowJobs.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-xs uppercase tracking-wider font-bold text-[var(--brand-gold-deep)]">
              Tomorrow ({tomorrowStr}) — {tomorrowJobs.length} job{tomorrowJobs.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="space-y-2">
            {tomorrowJobs.map((b) => <Row key={b.id} b={b} />)}
          </div>
        </div>
      )}

      {todayJobs.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-xs uppercase tracking-wider font-bold text-[var(--brand-teal)]">
              Today ({todayStr}) — {todayJobs.length} job{todayJobs.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="space-y-2">
            {todayJobs.map((b) => <Row key={b.id} b={b} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function SendConfirmationButton({ booking }) {
  const consented = booking.consent_communications === true;
  const firstName = (booking.full_name || "").split(" ")[0] || "there";
  const serviceLabel = booking.service_type === "deep" ? "Deep Cleaning" : "Standard Cleaning";
  const timeLabel = (booking.preferred_time || "").replace(/_/g, " ");
  const subject = encodeURIComponent(
    `Your ${serviceLabel} is Confirmed — ${booking.preferred_date} | Top-Notch Pro Solutions`
  );
  const body = encodeURIComponent(
    `Hi ${firstName},\n\n` +
    `Great news — your booking is confirmed and we've received your deposit. Here's a recap:\n\n` +
    `  • Service: ${serviceLabel}\n` +
    `  • Home: ${booking.bedrooms} bedroom${booking.bedrooms === 1 ? "" : "s"} / ${booking.bathrooms} bathroom${booking.bathrooms === 1 ? "" : "s"}\n` +
    `  • Date: ${booking.preferred_date}\n` +
    `  • Time window: ${timeLabel}\n` +
    `  • Address: ${booking.address}, ${booking.city}, ${booking.state} ${booking.zip_code}\n` +
    `  • Service estimate: $${(booking.service_estimate || 0).toFixed(2)}\n` +
    `  • Deposit received: $50.00\n` +
    `  • Remaining balance (invoiced after service): $${(booking.remaining_balance || 0).toFixed(2)}\n\n` +
    `Our team will arrive within your selected time window. If anything comes up beforehand, just reply to this email or call us at ${BRAND.phone}.\n\n` +
    `Thanks for choosing Top-Notch Pro Solutions — we can't wait to leave your home looking and feeling brand new.\n\n` +
    `— The Top-Notch Pro Solutions Team\n` +
    `${BRAND.phone} • ${BRAND.email || "topnotchprosolutions@gmail.com"}\n` +
    `${BRAND.legalName} · Evansville, IN\n\n` +
    `---\nYou are receiving this because you opted in to booking communications when scheduling your service. Reply STOP to opt out.`
  );
  const mailto = `mailto:${booking.email}?subject=${subject}&body=${body}`;

  if (!consented) {
    return (
      <div className="mt-3 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2" data-testid="no-consent-warning-confirmation">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Customer did not opt in to email/text communications. Use phone instead.</span>
      </div>
    );
  }
  return (
    <a
      href={mailto}
      className="mt-3 inline-flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-lg bg-[var(--brand-teal)] hover:bg-[var(--brand-teal-deep)] text-white text-xs font-bold uppercase tracking-wider transition"
      data-testid="send-confirmation-btn"
    >
      <Mail className="w-4 h-4" /> Send Booking Confirmation Email
    </a>
  );
}

function SendReminderButton({ booking, compact = false }) {
  const consented = booking.consent_communications === true;
  const firstName = (booking.full_name || "").split(" ")[0] || "there";
  const serviceLabel = booking.service_type === "deep" ? "Deep Cleaning" : "Standard Cleaning";
  const timeLabel = (booking.preferred_time || "").replace(/_/g, " ");
  const subject = encodeURIComponent(
    `Reminder: Your ${serviceLabel} is tomorrow (${booking.preferred_date})`
  );
  const body = encodeURIComponent(
    `Hi ${firstName},\n\n` +
    `Just a quick reminder that we're scheduled to be at your home tomorrow:\n\n` +
    `  • Date: ${booking.preferred_date}\n` +
    `  • Time window: ${timeLabel}\n` +
    `  • Service: ${serviceLabel}\n` +
    `  • Address: ${booking.address}, ${booking.city}\n\n` +
    `A few quick things to help us deliver the best result:\n` +
    `  • Please leave any gate or door codes accessible (or text them to ${BRAND.phone})\n` +
    `  • Secure pets in a safe area if needed\n` +
    `  • Let us know any special focus areas you'd like us to prioritize\n\n` +
    `Need to reschedule? Just reply or call ${BRAND.phone} ASAP.\n\n` +
    `Looking forward to it!\n— Top-Notch Pro Solutions\n${BRAND.phone}\n\n` +
    `---\nReply STOP to opt out of reminders.`
  );
  const mailto = `mailto:${booking.email}?subject=${subject}&body=${body}`;
  const smsBody = encodeURIComponent(
    `Hi ${firstName}, friendly reminder from Top-Notch Pro Solutions — we're cleaning your home tomorrow (${booking.preferred_date}, ${timeLabel}). Reply or call ${BRAND.phone} if anything changes. — TNPS`
  );
  const smsHref = `sms:${booking.phone}?&body=${smsBody}`;

  if (!consented) {
    if (compact) return null;
    return (
      <div className="mt-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800 flex items-start gap-2" data-testid="no-consent-warning-reminder">
        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>No email/text consent — call only.</span>
      </div>
    );
  }
  if (compact) {
    return (
      <div className="flex gap-1.5">
        <a href={mailto} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-[var(--brand-teal)] hover:bg-[var(--brand-teal-deep)] text-white text-[10px] font-bold uppercase tracking-wider transition" data-testid={`send-reminder-email-${booking.id}`}>
          <Mail className="w-3 h-3" /> Email
        </a>
        <a href={smsHref} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-[var(--brand-gold)] hover:bg-[var(--brand-gold-deep)] text-white text-[10px] font-bold uppercase tracking-wider transition" data-testid={`send-reminder-sms-${booking.id}`}>
          <PhoneIcon className="w-3 h-3" /> Text
        </a>
      </div>
    );
  }
  return (
    <div className="mt-3 grid grid-cols-2 gap-2">
      <a href={mailto} className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-[var(--brand-teal)] hover:bg-[var(--brand-teal-deep)] text-white text-xs font-bold uppercase tracking-wider transition" data-testid="send-reminder-email-btn">
        <Mail className="w-4 h-4" /> Email Reminder
      </a>
      <a href={smsHref} className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-[var(--brand-gold)] hover:bg-[var(--brand-gold-deep)] text-white text-xs font-bold uppercase tracking-wider transition" data-testid="send-reminder-sms-btn">
        <PhoneIcon className="w-4 h-4" /> Text Reminder
      </a>
    </div>
  );
}

function PromoSettingsPanel({ settings, onSaved }) {
  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState(settings.weekly_slots_left);
  const [enabled, setEnabled] = useState(settings.scarcity_enabled);
  const [customMsg, setCustomMsg] = useState(settings.custom_message || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSlots(settings.weekly_slots_left);
    setEnabled(settings.scarcity_enabled);
    setCustomMsg(settings.custom_message || "");
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch("/admin/settings/promo", {
        weekly_slots_left: parseInt(slots, 10),
        scarcity_enabled: enabled,
        custom_message: customMsg.trim() || null,
      });
      onSaved(res.data);
      toast.success("Promo settings updated");
    } catch {
      toast.error("Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const previewText = customMsg.trim() || `Only ${slots} weekly slot${slots === 1 ? "" : "s"} left this week in your area`;

  return (
    <div className="card-elevated mb-8 overflow-hidden" data-testid="promo-settings-panel">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-4 flex items-center justify-between bg-gradient-to-r from-[var(--brand-teal)] to-[var(--brand-teal-deep)] text-white"
        data-testid="promo-settings-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
            <Flame className="w-4 h-4 text-[var(--brand-gold)]" />
          </div>
          <div className="text-left">
            <div className="font-bold text-sm" style={{ fontFamily: "Outfit" }}>Scarcity Promo Banner</div>
            <div className="text-[11px] text-white/70">
              {enabled ? `Live • "${previewText}"` : "Hidden from site"}
            </div>
          </div>
        </div>
        <Settings className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="p-6 space-y-5 bg-white">
          <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--brand-cream-soft)]">
            <div>
              <div className="font-bold text-sm text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>Show on Quote Page</div>
              <div className="text-xs text-slate-500">Toggle visibility of the scarcity cue under the promo banner.</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="sr-only peer"
                data-testid="promo-enabled-toggle"
              />
              <div className="w-11 h-6 bg-slate-300 rounded-full peer-checked:bg-[var(--brand-teal)] transition-colors relative">
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${enabled ? "translate-x-5" : ""}`} />
              </div>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-[var(--brand-teal)] mb-2 uppercase tracking-wider">Weekly Slots Left</label>
              <input
                type="number"
                min="0"
                max="50"
                value={slots}
                onChange={(e) => setSlots(e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-[#dcd5c6] rounded-lg focus:outline-none focus:border-[var(--brand-teal)]"
                data-testid="promo-slots-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">Used when no custom message is set.</p>
            </div>
            <div>
              <label className="block text-xs font-bold text-[var(--brand-teal)] mb-2 uppercase tracking-wider">Custom Message (optional)</label>
              <input
                type="text"
                value={customMsg}
                onChange={(e) => setCustomMsg(e.target.value)}
                placeholder="Only 3 weekly slots left this week in your area"
                className="w-full px-3 py-2.5 border-2 border-[#dcd5c6] rounded-lg focus:outline-none focus:border-[var(--brand-teal)]"
                data-testid="promo-custom-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">If set, overrides the auto-generated text.</p>
            </div>
          </div>

          <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-center gap-2 text-sm text-red-700">
            <Flame className="w-4 h-4 flex-shrink-0" />
            <span><strong>Preview:</strong> {previewText}</span>
          </div>

          <button onClick={save} disabled={saving} className="btn-accent" data-testid="promo-save-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      )}
    </div>
  );
}


function RemainderPaymentLink({ booking }) {
  // Use auto-generated link if available; otherwise allow manual generation.
  const [link, setLink] = useState(booking.remainder_payment_url || null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Re-sync when a different booking is opened
  useEffect(() => { setLink(booking.remainder_payment_url || null); }, [booking.id, booking.remainder_payment_url]);

  const fullyPaid = booking.payment_status === "fully_paid";
  const remainder = booking.remaining_balance;
  const canSend = !fullyPaid && remainder > 0;
  const autoGenerated = Boolean(booking.remainder_payment_url);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/admin/payments/checkout/remainder", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      setLink(res.data.url);
      toast.success("Payment link generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate link");
    } finally {
      setGenerating(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Couldn't copy — long-press the link to copy manually");
    }
  };

  if (fullyPaid) {
    return (
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3" data-testid="remainder-fully-paid">
        <BadgeCheck className="w-5 h-5 text-emerald-600" />
        <div className="text-sm">
          <div className="font-bold text-emerald-800">Fully Paid</div>
          <div className="text-xs text-emerald-700">Customer paid the remaining ${remainder.toFixed(2)}.</div>
        </div>
      </div>
    );
  }

  if (!canSend) {
    return (
      <div className="rounded-lg bg-slate-100 border border-slate-200 p-4 text-sm text-slate-600" data-testid="remainder-none-due">
        No remaining balance due.
      </div>
    );
  }

  if (IS_QUICKBOOKS) {
    // Manual QB-invoice flow: no auto-generated link, just a clear instruction.
    return (
      <div className="rounded-xl border-2 border-[#2CA01C]/40 bg-gradient-to-br from-white to-emerald-50 p-5" data-testid="remainder-qb-block">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-[#2CA01C] text-white flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-[var(--brand-teal)] text-sm" style={{ fontFamily: "Outfit" }}>
              Send Remaining Balance via QuickBooks
            </div>
            <div className="text-xs text-slate-600 mt-0.5">${remainder.toFixed(2)} due from {booking.full_name}</div>
          </div>
        </div>
        <ol className="text-xs text-slate-700 space-y-1.5 list-decimal list-inside leading-relaxed mb-3">
          <li>Open your <strong>QuickBooks Online dashboard</strong></li>
          <li>Create a new invoice for <strong>{booking.email}</strong> for <strong>${remainder.toFixed(2)}</strong></li>
          <li>QuickBooks emails the customer a Pay-Now link automatically</li>
          <li>Once they pay, come back here and click <strong>"Mark Fully Paid"</strong> above</li>
        </ol>
        <a
          href="https://app.qbo.intuit.com/app/invoice"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-[#2CA01C] hover:bg-[#258D17] text-white text-sm font-bold transition"
          data-testid="open-qb-invoice-btn"
        >
          <ArrowUpRight className="w-4 h-4" /> Open QuickBooks Invoice
        </a>
      </div>
    );
  }

  const customerEmail = booking.email;
  const subject = encodeURIComponent(`Payment link for your ${booking.service_type} cleaning — Top-Notch Pro Solutions`);
  const body = encodeURIComponent(
    `Hi ${booking.full_name.split(" ")[0]},\n\nThank you so much for trusting Top-Notch Pro Solutions. Your service is complete!\n\n` +
    `Please use this secure Stripe link to pay your remaining balance of $${remainder.toFixed(2)}:\n\n${link || "[link will appear here]"}\n\n` +
    `Cards, Apple Pay, and Google Pay all accepted.\n\nThanks again!\n— Top-Notch Pro Solutions`
  );
  const mailto = link ? `mailto:${customerEmail}?subject=${subject}&body=${body}` : "";

  return (
    <div className="rounded-xl border-2 border-[var(--brand-gold)] bg-gradient-to-br from-white to-[var(--brand-cream-soft)] p-5" data-testid="remainder-payment-link">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-[var(--brand-gold)] text-white flex items-center justify-center flex-shrink-0">
          <CreditCard className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="font-bold text-[var(--brand-teal)] text-sm" style={{ fontFamily: "Outfit" }}>Remaining Balance Payment</div>
          <div className="text-xs text-slate-600 mt-0.5">${remainder.toFixed(2)} due from customer</div>
        </div>
        {autoGenerated && (
          <span className="text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full flex items-center gap-1 flex-shrink-0">
            <CheckCircle2 className="w-3 h-3" /> Auto-Ready
          </span>
        )}
      </div>

      {!link ? (
        <>
          <p className="text-xs text-slate-600 mb-3 leading-relaxed">
            Generate a one-time Stripe checkout link the customer can pay in one tap. <strong>Tip:</strong> Marking the booking as <strong>Completed</strong> generates this link automatically.
          </p>
          <button onClick={generate} disabled={generating} className="btn-primary w-full justify-center" data-testid="generate-payment-link-btn">
            <Link2 className="w-4 h-4" /> {generating ? "Generating…" : "Generate Payment Link"}
          </button>
        </>
      ) : (
        <>
          {autoGenerated && (
            <p className="text-xs text-[var(--brand-gold-deep)] mb-2 leading-relaxed font-semibold flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Auto-generated when you marked this booking completed.
            </p>
          )}
          <div className="rounded-lg bg-white border border-[#dcd5c6] p-3 break-all text-xs font-mono text-slate-700 mb-3" data-testid="generated-payment-link">
            {link}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={copy} className="btn-outline justify-center" style={{ padding: "10px 14px", fontSize: "13px" }} data-testid="copy-payment-link-btn">
              <Copy className="w-3.5 h-3.5" /> {copied ? "Copied!" : "Copy Link"}
            </button>
            <a href={mailto} className="btn-accent justify-center" style={{ padding: "10px 14px", fontSize: "13px" }} data-testid="email-payment-link-btn">
              <Mail className="w-3.5 h-3.5" /> Email to Customer
            </a>
          </div>
          <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
            Link is single-use and expires per Stripe defaults. Need a fresh one? <button onClick={generate} disabled={generating} className="text-[var(--brand-teal)] font-semibold underline" data-testid="regenerate-link-btn">Regenerate</button>.
          </p>
        </>
      )}
    </div>
  );
}

const TAG_OPTIONS = [
  "Residential Cleaning", "Deep Cleaning", "Standard Cleaning",
  "Handyman & Installation", "Property Improvement",
  "Recurring Cleaning & Yard Care", "Furniture Assembly", "Property Maintenance", "Other",
];
const SOURCE_OPTIONS = ["Thumbtack", "Google", "Yelp", "Facebook", "Direct", "Other"];

const EMPTY_T = { name: "", source: "Thumbtack", tag: "Residential Cleaning", text: "", featured: false, active: true, display_order: 100 };

function TestimonialsManager() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | <id>
  const [draft, setDraft] = useState(EMPTY_T);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/testimonials");
      setItems(res.data);
    } catch {
      toast.error("Failed to load testimonials");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const startNew = () => { setDraft(EMPTY_T); setEditing("new"); };
  const startEdit = (t) => { setDraft({ ...t }); setEditing(t.id); };
  const cancelEdit = () => { setEditing(null); setDraft(EMPTY_T); };

  const save = async () => {
    if (!draft.name.trim() || !draft.text.trim()) {
      toast.error("Name and review text are required");
      return;
    }
    try {
      if (editing === "new") {
        await api.post("/admin/testimonials", draft);
        toast.success("Review added");
      } else {
        await api.patch(`/admin/testimonials/${editing}`, draft);
        toast.success("Review updated");
      }
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggle = async (t, field) => {
    try {
      await api.patch(`/admin/testimonials/${t.id}`, { [field]: !t[field] });
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="card-elevated mb-8 overflow-hidden" data-testid="testimonials-manager">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-4 flex items-center justify-between bg-gradient-to-r from-[var(--brand-gold)] to-[var(--brand-gold-deep)] text-white"
        data-testid="testimonials-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
            <MessageSquareQuote className="w-4 h-4 text-white" />
          </div>
          <div className="text-left">
            <div className="font-bold text-sm" style={{ fontFamily: "Outfit" }}>Customer Testimonials</div>
            <div className="text-[11px] text-white/85">{items.length || "—"} reviews · shown live on the homepage</div>
          </div>
        </div>
        <Settings className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="p-6 bg-white space-y-4">
          {!editing && (
            <button onClick={startNew} className="btn-primary" data-testid="testimonial-add-btn">
              <Plus className="w-4 h-4" /> Add New Review
            </button>
          )}

          {editing && (
            <div className="rounded-xl border-2 border-[var(--brand-teal)] bg-[var(--brand-cream-soft)] p-5 space-y-4" data-testid="testimonial-edit-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--brand-teal)] mb-1.5">Client Name</label>
                  <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="form-input-admin" placeholder="Jane D." data-testid="t-input-name" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--brand-teal)] mb-1.5">Source</label>
                  <select value={draft.source} onChange={(e) => setDraft({ ...draft, source: e.target.value })} className="form-input-admin" data-testid="t-input-source">
                    {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--brand-teal)] mb-1.5">Service Tag</label>
                  <select value={draft.tag} onChange={(e) => setDraft({ ...draft, tag: e.target.value })} className="form-input-admin" data-testid="t-input-tag">
                    {TAG_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--brand-teal)] mb-1.5">Review Text</label>
                <textarea
                  value={draft.text}
                  onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                  className="form-input-admin min-h-[120px] resize-y"
                  placeholder="Paste the customer's review here..."
                  data-testid="t-input-text"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--brand-teal)] mb-1.5">Display Order</label>
                  <input type="number" value={draft.display_order} onChange={(e) => setDraft({ ...draft, display_order: parseInt(e.target.value || "100", 10) })} className="form-input-admin" data-testid="t-input-order" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer pb-2">
                  <input type="checkbox" checked={draft.featured} onChange={(e) => setDraft({ ...draft, featured: e.target.checked })} className="w-4 h-4 accent-[var(--brand-gold)]" data-testid="t-input-featured" />
                  <span className="text-sm text-slate-700">Featured (big card at top)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer pb-2">
                  <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} className="w-4 h-4 accent-[var(--brand-teal)]" data-testid="t-input-active" />
                  <span className="text-sm text-slate-700">Active (visible on site)</span>
                </label>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={save} className="btn-accent" data-testid="t-save-btn">
                  <Save className="w-4 h-4" /> {editing === "new" ? "Add Review" : "Save Changes"}
                </button>
                <button onClick={cancelEdit} className="btn-outline" data-testid="t-cancel-btn">Cancel</button>
              </div>
              {draft.featured && <p className="text-[11px] text-[var(--brand-gold-deep)]">⭐ Setting this as featured will un-feature any other review (only one featured at a time).</p>}
            </div>
          )}

          {loading ? (
            <div className="py-8 text-center text-sm text-slate-500">Loading reviews...</div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">No reviews yet — add one to populate the homepage.</div>
          ) : (
            <div className="space-y-3">
              {items.map((t) => (
                <div
                  key={t.id}
                  className={`rounded-xl border p-4 transition ${t.featured ? "border-[var(--brand-gold)] bg-[var(--brand-gold)]/5" : "border-[#dcd5c6] bg-white"} ${!t.active ? "opacity-50" : ""}`}
                  data-testid={`t-row-${t.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-bold text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>{t.name}</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[var(--brand-teal)] text-white">{t.source}</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[var(--brand-gold)]/15 text-[var(--brand-gold-deep)]">{t.tag}</span>
                        {t.featured && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[var(--brand-gold)] text-white flex items-center gap-1"><Star className="w-2.5 h-2.5 fill-white" /> Featured</span>}
                        {!t.active && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-300 text-slate-700">Hidden</span>}
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2">"{t.text}"</p>
                      <div className="text-[10px] text-slate-400 mt-1">Order: {t.display_order}</div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => toggle(t, "featured")} title={t.featured ? "Unfeature" : "Set as featured"} className="p-2 rounded-lg hover:bg-[var(--brand-gold)]/15 text-[var(--brand-gold-deep)]" data-testid={`t-toggle-feature-${t.id}`}>
                        <Star className={`w-4 h-4 ${t.featured ? "fill-[var(--brand-gold)]" : ""}`} />
                      </button>
                      <button onClick={() => toggle(t, "active")} title={t.active ? "Hide from site" : "Show on site"} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`t-toggle-active-${t.id}`}>
                        {t.active ? <EyeIcon className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button onClick={() => startEdit(t)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`t-edit-${t.id}`}>
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(t.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" data-testid={`t-delete-${t.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <style>{`.form-input-admin { width: 100%; padding: 10px 12px; border: 1.5px solid #dcd5c6; border-radius: 8px; background: white; font-family: inherit; color: #0f172a; font-size: 14px; transition: border-color 0.15s; }
      .form-input-admin:focus { outline: none; border-color: var(--brand-teal); box-shadow: 0 0 0 3px rgba(13,91,94,0.1); }`}</style>
    </div>
  );
}

const QR_STATUS_LABELS = {
  new:      { label: "New",      color: "bg-amber-100 text-amber-800 border-amber-300" },
  reviewed: { label: "Reviewed", color: "bg-blue-100 text-blue-800 border-blue-300" },
  quoted:   { label: "Quoted",   color: "bg-purple-100 text-purple-800 border-purple-300" },
  closed:   { label: "Closed",   color: "bg-slate-100 text-slate-600 border-slate-300" },
};

function QuoteRequestsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/quote-requests");
      setItems(res.data);
    } catch {
      toast.error("Failed to load quote requests");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      const res = await api.patch(`/admin/quote-requests/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
      if (selected?.id === id) setSelected(res.data);
    } catch { toast.error("Update failed"); }
  };

  const saveNotes = async (id, notes) => {
    try {
      await api.patch(`/admin/quote-requests/${id}`, { admin_notes: notes });
      toast.success("Notes saved");
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quote request?")) return;
    try {
      await api.delete(`/admin/quote-requests/${id}`);
      setSelected(null);
      load();
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const filtered = filter === "all" ? items : items.filter((q) => q.status === filter);

  return (
    <>
      <div className="card-elevated mt-8 overflow-hidden" data-testid="quote-requests-panel">
        <div className="p-5 border-b border-[#e6e0d4] flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
          <div>
            <h2 className="font-bold text-lg text-[var(--brand-teal)] flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <MessageSquareQuote className="w-5 h-5 text-[var(--brand-gold)]" /> Custom Quote Requests
            </h2>
            <p className="text-xs text-slate-500">Handyman, assembly, fixtures, improvements & property mgmt — {items.length} total</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-slate-400" />
            {["all", "new", "reviewed", "quoted", "closed"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`qr-filter-${s}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition ${
                  filter === s ? "bg-[var(--brand-gold)] text-white" : "bg-white border border-[#dcd5c6] text-slate-600 hover:border-[var(--brand-gold)]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="qr-empty-state">
            <MessageSquareQuote className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            No custom quote requests {filter !== "all" ? `in "${filter}"` : "yet"}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="qr-table">
              <thead className="bg-[var(--brand-cream-soft)] text-slate-600 uppercase text-xs">
                <tr>
                  <th className="px-5 py-3 text-left font-semibold">Customer</th>
                  <th className="px-5 py-3 text-left font-semibold">Service</th>
                  <th className="px-5 py-3 text-left font-semibold">Timeline</th>
                  <th className="px-5 py-3 text-center font-semibold">Photos</th>
                  <th className="px-5 py-3 text-left font-semibold">Status</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((q) => {
                  const status = QR_STATUS_LABELS[q.status] || QR_STATUS_LABELS.new;
                  return (
                    <tr key={q.id} className="border-t border-[#e6e0d4] hover:bg-[var(--brand-cream-soft)]/40" data-testid={`qr-row-${q.id}`}>
                      <td className="px-5 py-4">
                        <div className="font-semibold text-[var(--brand-teal)]">{q.full_name}</div>
                        <div className="text-xs text-slate-500">{q.email}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-semibold text-slate-700">{q.service_type}</div>
                      </td>
                      <td className="px-5 py-4 text-xs text-slate-700 capitalize">{(q.timeline || "flexible").replace(/_/g, " ")}</td>
                      <td className="px-5 py-4 text-center">
                        {q.photos?.length > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--brand-gold)]/15 text-[var(--brand-gold-deep)] text-xs font-bold">
                            <ImageIcon className="w-3 h-3" /> {q.photos.length}
                          </span>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold border ${status.color}`}>{status.label}</span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button onClick={() => setSelected(q)} className="btn-outline" style={{ padding: "6px 14px", fontSize: "12px" }} data-testid={`qr-view-${q.id}`}>
                          <Eye className="w-3.5 h-3.5" /> View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <QuoteRequestDetail
          request={selected}
          onClose={() => setSelected(null)}
          onStatus={(s) => updateStatus(selected.id, s)}
          onSaveNotes={(n) => saveNotes(selected.id, n)}
          onDelete={() => remove(selected.id)}
        />
      )}
    </>
  );
}

function QuoteRequestDetail({ request, onClose, onStatus, onSaveNotes, onDelete }) {
  const [notes, setNotes] = useState(request.admin_notes || "");
  const [lightboxIdx, setLightboxIdx] = useState(null);
  useEffect(() => { setNotes(request.admin_notes || ""); }, [request.id]);

  const subject = encodeURIComponent(`Your quote for ${request.service_type} — Top-Notch Pro Solutions`);
  const body = encodeURIComponent(
    `Hi ${request.full_name.split(" ")[0]},\n\nThanks for reaching out about your ${request.service_type.toLowerCase()} project. ` +
    `Based on your description${request.photos?.length ? ' and photos' : ''}, here is our quote:\n\n[YOUR QUOTE HERE]\n\n` +
    `Let us know if you'd like to move forward — we can be on site as soon as you'd like.\n\nThanks again,\nTop-Notch Pro Solutions`
  );
  const mailto = `mailto:${request.email}?subject=${subject}&body=${body}`;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="qr-detail-modal">
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto animate-[fadeUp_0.25s_ease]">
        <div className="sticky top-0 z-10 bg-white border-b border-[#e6e0d4] px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-[var(--brand-teal)] text-lg" style={{ fontFamily: "Outfit" }}>Quote Request</h2>
            <p className="text-xs font-mono text-slate-400">{request.id}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="qr-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Status switcher */}
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Status</div>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(QR_STATUS_LABELS).map(([s, info]) => (
                <button
                  key={s}
                  onClick={() => onStatus(s)}
                  className={`px-2 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border-2 transition ${
                    request.status === s ? "border-[var(--brand-gold)] bg-[var(--brand-gold)] text-white" : "border-[#dcd5c6] bg-white text-slate-600 hover:border-[var(--brand-gold)]"
                  }`}
                  data-testid={`qr-set-status-${s}`}
                >
                  {info.label}
                </button>
              ))}
            </div>
          </div>

          {/* Service + Timeline */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-[var(--brand-cream-soft)] p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Service</div>
              <div className="font-bold text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>{request.service_type}</div>
            </div>
            <div className="rounded-lg bg-[var(--brand-cream-soft)] p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Timeline</div>
              <div className="font-bold text-slate-700 capitalize">{(request.timeline || "flexible").replace(/_/g, " ")}</div>
            </div>
          </div>

          {/* Customer */}
          <div className="rounded-lg bg-[var(--brand-cream-soft)] p-4 space-y-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Customer</div>
            <div className="font-bold text-[var(--brand-teal)] text-base" style={{ fontFamily: "Outfit" }}>{request.full_name}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <a href={`mailto:${request.email}`} className="flex items-center gap-2 text-slate-700 hover:text-[var(--brand-teal)]"><Mail className="w-3.5 h-3.5 text-[var(--brand-gold)]" /> {request.email}</a>
              <a href={`tel:${request.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-[var(--brand-teal)]"><PhoneIcon className="w-3.5 h-3.5 text-[var(--brand-gold)]" /> {request.phone}</a>
              <span className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="w-3.5 h-3.5 text-[var(--brand-gold)]" /> Prefers <strong>{request.preferred_contact}</strong></span>
              {(request.address || request.city) && (
                <span className="flex items-center gap-2 text-slate-700"><MapPin className="w-3.5 h-3.5 text-[var(--brand-gold)]" /> {[request.address, request.city, request.zip_code].filter(Boolean).join(", ")}</span>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Project Description</div>
            <div className="rounded-lg bg-white border border-[#dcd5c6] p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="qr-detail-description">
              {request.project_description}
            </div>
          </div>

          {/* Photos */}
          {request.photos?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Photos ({request.photos.length})</div>
              <div className="grid grid-cols-3 gap-2">
                {request.photos.map((src, i) => (
                  <button
                    key={`p-${i}`}
                    type="button"
                    onClick={() => setLightboxIdx(i)}
                    className="aspect-square rounded-lg overflow-hidden border border-[#dcd5c6] hover:ring-2 hover:ring-[var(--brand-gold)] transition"
                    data-testid={`qr-photo-${i}`}
                  >
                    <img src={src} alt={`Project ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Admin notes */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Your Notes (internal)</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Quote amount, scheduling notes, follow-up date..."
              className="w-full px-3 py-2 border-2 border-[#dcd5c6] rounded-lg min-h-[80px] resize-y text-sm focus:outline-none focus:border-[var(--brand-teal)]"
              data-testid="qr-notes"
            />
            <button onClick={() => onSaveNotes(notes)} className="btn-primary mt-2" style={{ padding: "8px 16px", fontSize: "13px" }} data-testid="qr-save-notes">
              <Save className="w-3.5 h-3.5" /> Save Notes
            </button>
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-2 gap-2 pt-2">
            <a href={mailto} className="btn-accent justify-center" data-testid="qr-email-customer">
              <Mail className="w-4 h-4" /> Email Quote
            </a>
            <a href={`tel:${request.phone}`} className="btn-outline justify-center" data-testid="qr-call-customer">
              <PhoneIcon className="w-4 h-4" /> Call Customer
            </a>
          </div>

          <button onClick={onDelete} className="w-full px-4 py-3 rounded-lg border-2 border-red-300 text-red-600 font-semibold hover:bg-red-50 transition flex items-center justify-center gap-2 mt-4" data-testid="qr-delete">
            <Trash2 className="w-4 h-4" /> Delete Request
          </button>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-6"
          onClick={() => setLightboxIdx(null)}
          data-testid="qr-lightbox"
        >
          <button onClick={() => setLightboxIdx(null)} className="absolute top-6 right-6 text-white p-2 rounded-full bg-white/10 hover:bg-white/20"><X className="w-5 h-5" /></button>
          <img src={request.photos[lightboxIdx]} alt={`Full size ${lightboxIdx + 1}`} className="max-h-full max-w-full object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
}

